import { BehaviorSubject } from 'rxjs';

export class DocumentsViewSettings {
  private readonly STORAGE_KEY = 'documents-view-settings';
  private viewMode = new BehaviorSubject<DocumentsViewMode>(DocumentsViewMode.card);
  private filters = new BehaviorSubject<DisplayableFilters>(new DisplayableFilters());
  private columns = new BehaviorSubject<DisplayableColumns>(new DisplayableColumns());
  private sorting = new BehaviorSubject<string>('lastModifiedDateTime.desc');
  private period = new BehaviorSubject<number>(31);

  viewMode$ = this.viewMode.asObservable();
  filters$ = this.filters.asObservable();
  columns$ = this.columns.asObservable();
  sort$ = this.sorting.asObservable();
  period$ = this.period.asObservable();

  constructor() {
    const storageItem = localStorage.getItem(this.STORAGE_KEY);
    if (storageItem) {
      const storedViewSettings = JSON.parse(storageItem);
      if (storedViewSettings.viewMode) {
        this.updateViewMode(storedViewSettings.viewMode);
      }

      //merge with defaults since the object stored in the local storage could be older and not contain filters attribute
      if (storedViewSettings.filters) {
        this.updateFilters({ ...this.filters.value, ...storedViewSettings.filters });
      }
      if (storedViewSettings.columns) {
        this.updateColumns({ ...this.columns.value, ...storedViewSettings.columns });
      }

      if (storedViewSettings.sorting) {
        this.updateSorting(storedViewSettings.sorting);
      }
      if (storedViewSettings.period) {
        this.updatePeriod(storedViewSettings.period);
      }
    }
  }

  getViewMode(): DocumentsViewMode {
    return this.viewMode.value || DocumentsViewMode.card;
  }

  updateViewMode(newValue: DocumentsViewMode): void {
    this.viewMode.next(newValue);
    this.storeValue();
  }

  updateFilters(filters: DisplayableFilters): void {
    this.filters.next(filters);
    this.storeValue();
  }

  updateColumns(columns: DisplayableColumns): void {
    this.columns.next(columns);
    this.storeValue();
  }

  getSorting(): string {
    return this.sorting.value;
  }

  updateSorting(sortBy: string): void {
    this.sorting.next(sortBy);
    this.storeValue();
  }

  getPeriod(): number {
    return this.period.value;
  }

  updatePeriod(period: number): void {
    this.period.next(period);
    this.storeValue();
  }

  private storeValue(): void {
    localStorage.setItem(
      this.STORAGE_KEY,
      JSON.stringify({
        viewMode: this.viewMode.value,
        filters: this.filters.value,
        columns: this.columns.value,
        sorting: this.sorting.value,
        period: this.period.value
      })
    );
  }
}

export class DisplayableFilters {
  orderNumber = new DisplayableItem('filter-order-number', 'freightDocument.orderNumber', true);
  client = new DisplayableItem('filter-client', 'role.client', false);
  consignor = new DisplayableItem('filter-consignor', 'role.consignor', true);
  consignee = new DisplayableItem('filter-consignee', 'role.consignee', true);
  carrier = new DisplayableItem('filter-carrier', 'role.carrier', true);
  subcontractor = new DisplayableItem('filter-subcontractor', 'role.subcontractor', false);
  vehicle = new DisplayableItem('filter-label', 'vehicle.label', false);
  reference = new DisplayableItem('filter-reference', 'transport.reference', true);
  signedBy = new DisplayableItem('filter-signedBy', 'freightDocument.signedBy', false);
  establishedDate = new DisplayableItem('filter-establishedDate', 'freightDocument.establishedDate', true);
  actualDeliveryDate = new DisplayableItem('filter-deliveryDateActual', 'freightDocument.deliveryDateActual', false);
  withModifiedGoods = new DisplayableItem('filter-withGoodsAdjusted', 'freightDocument.filter.withGoodsAdjusted', false);
  withRtis = new DisplayableItem('filter-withRtis', 'freightDocument.filter.withRtis', false);
  flags = new DisplayableItem('filter-flags', 'freightDocument.filter.flags', true);
  withObservations = new DisplayableItem('filter-withObservations', 'observations.withObservations', false);
}

export class DisplayableColumns {
  client = new DisplayableItem('column-client', 'role.client', true);
  consignor = new DisplayableItem('column-consignor', 'role.consignor', true);
  consignee = new DisplayableItem('column-consignee', 'role.consignee', true);
  carrier = new DisplayableItem('column-carrier', 'role.carrier', true);
  subcontractor = new DisplayableItem('column-subcontractor', 'role.subcontractor', false);
  vehicle = new DisplayableItem('column-vehicle', 'vehicle.label', false);
  placeOfTakingOver = new DisplayableItem('column-placeoftakingover', 'role.placeoftakingover', true);
  placeOfDelivery = new DisplayableItem('column-placeofdelivery', 'role.placeofdelivery', true);
  agreedDateOfTakingOver = new DisplayableItem('column-pickupDate', 'freightDocument.pickupDate', true);
  agreedDateOfDelivery = new DisplayableItem('column-deliveryDate', 'freightDocument.deliveryDate', true);
  establishedDate = new DisplayableItem('column-establishedDate', 'freightDocument.establishedDate', false);
  lastModified = new DisplayableItem('column-lastModified', 'general.lastModified', true);
  references = new DisplayableItem('column-references', 'tfDrive.signingInspections.references', false);
  flags = new DisplayableItem('column-flags', 'freightDocument.flags.title', true);
}

export class DisplayableItem {
  id: string;
  label: string;
  isDisplayed: boolean;

  constructor(id: string, label: string, isDisplayed: boolean) {
    this.id = id;
    this.label = label;
    this.isDisplayed = isDisplayed;
  }
}

export enum DocumentsViewMode {
  table = 'table', card = 'card'
}
